import { faTrashAlt } from "@fortawesome/free-solid-svg-icons/faTrashAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import styles from "components/animal/AnimalIdentification.module.scss";
import { animalMessages } from "domain/animal/animalMessages";
import { LicensingAnimalIdentificationType } from "generatedQueries/upsertAnimalMutation.graphql";
import { UpsertAnimalInput } from "domain/animal/upsertAnimal";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { Button } from "reactstrap";
import { Control, UseFormRegister } from "react-hook-form";
import { ISelectOption } from "shared/components/ReactSelect";

import { FormSelect } from "shared/components/newFormInputs/FormSelect";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { TranslatedUseControllerOptions, TranslatedRegisterOptions } from "shared/validations/useTranslatedValidations";

type TypePath = `identifications.${number}.type`;
type ValuePath = `identifications.${number}.value`;

interface IProps {
    index: number;
    onRemoveClicked: () => void;
    control: Control<UpsertAnimalInput>;
    register: UseFormRegister<UpsertAnimalInput>;
    typeRules?: TranslatedUseControllerOptions<UpsertAnimalInput, TypePath>;
    valueRules?: TranslatedRegisterOptions<UpsertAnimalInput, ValuePath>;
}

export const AnimalIdentification = ({
    index,
    onRemoveClicked,
    control,
    register,
    typeRules,
    valueRules,
}: IProps) => {
    const { formatMessage } = useIntl();

    const animalIdentificationTypes = useMemo<ISelectOption[]>(() => {
        return [
            { label: formatMessage(animalMessages.microchipLabel), value: "MICROCHIP" },
            { label: formatMessage(animalMessages.tattooLabel), value: "TATTOO" },
            { label: formatMessage(animalMessages.licenceTagNumberLabel), value: "LICENCE" },
        ];
    }, [formatMessage]);

    const typeName = `identifications.${index}.type` as TypePath;
    const valueName = `identifications.${index}.value` as ValuePath;

    return (
        <div className="form-group mb-3" role="group" aria-label={formatMessage(animalMessages.identificationLabel)}>
            <div className="input-group align-items-start">
                <div className="input-group-prepend" style={{ minWidth: "150px" }}>
                    <FormSelect<UpsertAnimalInput, TypePath>
                        name={typeName}
                        control={control}
                        className={classNames(styles.type, "input-animal-identification-type", "w-100")}
                        isSearchable={false}
                        options={animalIdentificationTypes}
                        aria-label={formatMessage(animalMessages.identificationLabel)}
                        rules={typeRules}
                    />
                </div>

                <TextBox<UpsertAnimalInput, ValuePath>
                    name={valueName}
                    control={control}
                    register={register}
                    className={classNames("form-control", styles.type, "input-animal-identification-value")}
                    type="text"
                    aria-label={formatMessage(animalMessages.identificationLabel)}
                    options={valueRules}
                />

                <div className="input-group-append">
                    <Button type="button" onClick={onRemoveClicked} aria-label={formatMessage(animalMessages.removeIdentificationTooltip)} color="secondary">
                        <FontAwesomeIcon icon={faTrashAlt} className="icon-button" aria-hidden="true" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export function isAnimalIdentificationType(value: number | string): value is LicensingAnimalIdentificationType {
    return value === "MICROCHIP" || value === "TATTOO" || value === "LICENCE";
}
